<template>
  <v-card color="white" :loading="loading" :disabled="loading">
    <v-card-title class="justify-center" style="background-color: #5E676E;">
      <div class="white--text text-center">休診日關閉空檔</div>
      <v-tooltip bottom max-width="360">
        <template v-slot:activator="{ on, attrs }">
          <v-icon small color="white" class="ml-2" v-bind="attrs" v-on="on">
            mdi-information-outline
          </v-icon>
        </template>
        <div>患者在 LINE、跨網頁、Google 地圖都選不到這天的時段</div>
        <div>只影響手動空檔、系統自動篩選模式的醫師</div>
      </v-tooltip>
    </v-card-title>

    <v-card-text class="py-6 black--text">
      <div v-if="loadFailed" class="text-center py-4">
        <div>休診日讀取失敗</div>
        <v-btn class="mt-3" outlined color="primary" @click="fetchClosures">重新讀取</v-btn>
      </div>

      <template v-else-if="loaded">
        <div v-if="!upcoming.length" class="text-center py-4">
          <div class="grey--text text--darken-1">目前沒有關閉空檔的日期</div>
          <v-btn class="mt-3" color="primary" @click="openAdd()">＋ 新增休診日</v-btn>
        </div>

        <template v-else>
          <div class="d-flex justify-end mb-3">
            <v-btn outlined color="primary" @click="openAdd()">＋ 新增休診日</v-btn>
          </div>
          <v-simple-table class="closure-table">
            <thead>
              <tr>
                <th class="closure-table__date">休診日</th>
                <th>關閉空檔的醫師</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              <tr v-for="closure in upcoming" :key="closure.date">
                <td class="closure-table__date">{{ formatClosureDate(closure.date) }}</td>
                <td>
                  <span :title="summarizeDoctorNames(closure.closed_doctors).title">
                    {{ summarizeDoctorNames(closure.closed_doctors).text }}
                  </span>
                </td>
                <td class="text-right closure-table__actions">
                  <v-btn text small color="tiffany-7" @click="editDate = closure.date">編輯</v-btn>
                  <v-btn text small color="tiffany-7" @click="reopenDate = closure.date">重新開放</v-btn>
                  <v-btn text small color="tiffany-7" @click="openAdd(closure)">複製</v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </template>

        <template v-if="ended.length">
          <button
            type="button"
            class="closure-ended-toggle mt-5"
            :aria-expanded="String(showEnded)"
            @click="showEnded = !showEnded"
          >
            <v-icon small>{{ showEnded ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            已結束的休診日（{{ ended.length }}）
          </button>
          <div v-if="showEnded" class="closure-ended mt-2">
            <v-simple-table class="closure-table" fixed-header>
              <thead>
                <tr>
                  <th class="closure-table__date">休診日</th>
                  <th>關閉空檔的醫師</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                <tr v-for="closure in ended" :key="closure.date" class="grey--text text--darken-1">
                  <td class="closure-table__date">{{ formatClosureDateWithYear(closure.date) }}</td>
                  <td>
                    <span :title="summarizeDoctorNames(closure.closed_doctors).title">
                      {{ summarizeDoctorNames(closure.closed_doctors).text }}
                    </span>
                  </td>
                  <td class="text-right closure-table__actions">
                    <v-btn text small color="tiffany-7" @click="openAdd(closure)">複製</v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </template>
      </template>
    </v-card-text>

    <slot-closure-add-dialog
      v-if="addOpen"
      v-model="addOpen"
      :doctors="doctors"
      :closed-dates="closedDates"
      :copy-source="copySource"
      :saving="saving"
      @submit="createClosures"
    />
    <slot-closure-edit-dialog
      v-if="editingClosure"
      :value="true"
      :closure="editingClosure"
      :doctors="doctors"
      :saving="saving"
      @input="editDate = null"
      @submit="updateClosure"
    />
    <confirm-dialog
      v-if="reopenDate"
      :value="true"
      :title="`重新開放 ${formatClosureDate(reopenDate)}的空檔？`"
      confirm-btn-text="重新開放"
      @input="reopenDate = null"
      @confirm="reopenClosure"
    />
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import {
  createSlotClosuresAPI,
  deleteSlotClosureAPI,
  getSlotClosuresAPI,
  updateSlotClosureAPI
} from "@/api";
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";
import SlotClosureAddDialog from "./SlotClosureAddDialog.vue";
import SlotClosureEditDialog from "./SlotClosureEditDialog.vue";
import {
  closureErrorMessage,
  formatClosureDate,
  formatClosureDateWithYear,
  summarizeDoctorNames
} from "@/utils/slotClosure";

export default {
  name: "SlotClosureSetting",

  components: {ConfirmDialog, SlotClosureAddDialog, SlotClosureEditDialog},

  data() {
    return {
      loading: false,
      loaded: false,
      loadFailed: false,
      saving: false,
      doctors: [],
      upcoming: [],
      ended: [],
      showEnded: false,
      addOpen: false,
      copySource: null,
      editDate: null,
      reopenDate: null,
      formatClosureDate,
      formatClosureDateWithYear,
      summarizeDoctorNames
    }
  },

  computed: {
    ...mapGetters(['axiosParamData']),
    closedDates() {
      return this.upcoming.map(closure => closure.date)
    },
    editingClosure() {
      return this.upcoming.find(closure => closure.date === this.editDate) || null
    }
  },

  mounted() {
    this.fetchClosures()
  },

  methods: {
    openAdd(copySource = null) {
      this.copySource = copySource
      this.addOpen = true
    },

    async fetchClosures() {
      this.loading = true
      try {
        const { data } = await getSlotClosuresAPI(this.axiosParamData)
        this.doctors = data.doctors
        this.upcoming = data.upcoming
        this.ended = data.ended
        this.loaded = true
        this.loadFailed = false
      } catch (e) {
        console.error(e)
        this.loadFailed = true
      } finally {
        this.loading = false
        this.$emit('loaded')
      }
    },

    // 失敗也要重抓：別人可能剛改過這天，清單要反映現況
    async save(request, onSuccess) {
      if (this.saving) return
      this.saving = true
      try {
        onSuccess((await request()).data)
      } catch (e) {
        console.error(e)
        const message = closureErrorMessage(e)
        if (message) this.$notify.error(message)
      } finally {
        this.saving = false
      }
      await this.fetchClosures()
    },

    createClosures(dates) {
      return this.save(
        () => createSlotClosuresAPI({ ...this.axiosParamData, dates }),
        () => {
          this.addOpen = false
          this.$notify.success(dates.length === 1
            ? `已關閉 ${formatClosureDate(dates[0].date)}的空檔`
            : `已關閉 ${dates.length} 天的空檔`)
        }
      )
    },

    updateClosure(payload) {
      return this.save(
        () => updateSlotClosureAPI({ ...this.axiosParamData, ...payload }),
        ({ reopened }) => {
          this.editDate = null
          this.$notify.success(reopened ? `已重新開放 ${formatClosureDate(payload.date)}的空檔` : '已儲存')
        }
      )
    },

    reopenClosure() {
      const date = this.reopenDate
      this.reopenDate = null
      return this.save(
        () => deleteSlotClosureAPI({ ...this.axiosParamData, date }),
        () => this.$notify.success(`已重新開放 ${formatClosureDate(date)}的空檔`)
      )
    }
  }
}
</script>

<style scoped>
.closure-table >>> thead th {
  background: #f5f5f5 !important;
  font-weight: 500;
  white-space: nowrap;
}

.closure-table__date {
  width: 170px;
  white-space: nowrap;
}

.closure-table__actions {
  white-space: nowrap;
}

.closure-ended-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #757575;
}

.closure-ended {
  max-height: 330px;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

@media (max-width: 599px) {
  .closure-table >>> th,
  .closure-table >>> td {
    padding: 8px !important;
  }

  .closure-table__date {
    width: auto;
  }

  .closure-table__actions {
    width: 1%;
  }

  .closure-table__actions .v-btn {
    display: flex;
    margin-left: auto;
  }
}
</style>
